import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { 
  Loader2, Sparkles, Eye, Brain, Building2, TreePine, Circle, Square, 
  BarChart3, Settings, Waves, RotateCcw, GitBranch, Calendar, 
  ArrowUpDown, Triangle, Table, Scale, TrendingUp, Activity,
  Network, Music, Rainbow, Grid3x3, Hexagon, Target, CheckSquare,
  Trophy, AlertTriangle, Users, BookOpen, Palette, Dumbbell,
  PieChart, Mountain, MapPin, Flame, Wind, Package, Box,
  type LucideIcon
} from "lucide-react";

interface VisualType {
  id: string;
  name: string;
  category: string;
  icon: LucideIcon;
}

const visualTypes: VisualType[] = [
  // Structure & Relationships
  { id: "mindmap", name: "Mindmap", category: "Structure & Relationships", icon: Brain },
  { id: "org-chart", name: "Org Chart", category: "Structure & Relationships", icon: Building2 },
  { id: "hierarchy", name: "Hierarchy/Tree", category: "Structure & Relationships", icon: TreePine },
  { id: "radial-wheel", name: "Radial Wheel", category: "Structure & Relationships", icon: Circle },
  { id: "circle-packing", name: "Circle Packing", category: "Structure & Relationships", icon: Circle },
  { id: "treemap", name: "Treemap", category: "Structure & Relationships", icon: Square },
  { id: "sunburst", name: "Sunburst", category: "Structure & Relationships", icon: Target },
  { id: "icicle", name: "Icicle", category: "Structure & Relationships", icon: Mountain },

  // Process & Flow
  { id: "flowchart", name: "Flowchart", category: "Process & Flow", icon: BarChart3 },
  { id: "process-diagram", name: "Process Diagram", category: "Process & Flow", icon: Settings },
  { id: "swimlane", name: "Swimlane", category: "Process & Flow", icon: Waves },
  { id: "bpmn", name: "BPMN Diagram", category: "Process & Flow", icon: RotateCcw },
  { id: "state-machine", name: "State Machine", category: "Process & Flow", icon: GitBranch },
  { id: "decision-tree", name: "Decision Tree", category: "Process & Flow", icon: TreePine },
  { id: "customer-journey", name: "Customer Journey", category: "Process & Flow", icon: MapPin },
  { id: "service-blueprint", name: "Service Blueprint", category: "Process & Flow", icon: BookOpen },
  { id: "roadmap", name: "Roadmap", category: "Process & Flow", icon: MapPin },
  { id: "timeline", name: "Timeline", category: "Process & Flow", icon: Calendar },
  { id: "spiral-timeline", name: "Spiral Timeline", category: "Process & Flow", icon: Activity },
  { id: "cycle-diagram", name: "Cycle Diagram", category: "Process & Flow", icon: RotateCcw },
  { id: "funnel", name: "Funnel", category: "Process & Flow", icon: Triangle },
  { id: "alluvial", name: "Alluvial/Sankey", category: "Process & Flow", icon: Waves },

  // Comparison & Part-to-whole
  { id: "table", name: "Table/Matrix", category: "Comparison & Part-to-whole", icon: Table },
  { id: "comparison-table", name: "Comparison Table", category: "Comparison & Part-to-whole", icon: Table },
  { id: "pros-cons", name: "Pros & Cons", category: "Comparison & Part-to-whole", icon: Scale },
  { id: "bar-chart", name: "Bar Chart", category: "Comparison & Part-to-whole", icon: BarChart3 },
  { id: "lollipop-chart", name: "Lollipop Chart", category: "Comparison & Part-to-whole", icon: Circle },
  { id: "dot-plot", name: "Dot Plot", category: "Comparison & Part-to-whole", icon: Circle },
  { id: "dumbbell-chart", name: "Dumbbell Chart", category: "Comparison & Part-to-whole", icon: Dumbbell },
  { id: "slopegraph", name: "Slopegraph", category: "Comparison & Part-to-whole", icon: TrendingUp },
  { id: "bullet-chart", name: "Bullet Chart", category: "Comparison & Part-to-whole", icon: Target },
  { id: "waterfall-chart", name: "Waterfall Chart", category: "Comparison & Part-to-whole", icon: Mountain },
  { id: "marimekko", name: "Marimekko", category: "Comparison & Part-to-whole", icon: Palette },
  { id: "waffle-chart", name: "Waffle Chart", category: "Comparison & Part-to-whole", icon: Grid3x3 },
  { id: "pie-chart", name: "Pie Chart", category: "Comparison & Part-to-whole", icon: PieChart },
  { id: "donut-chart", name: "Donut Chart", category: "Comparison & Part-to-whole", icon: Circle },
  { id: "polar-area", name: "Polar Area", category: "Comparison & Part-to-whole", icon: Target },
  { id: "radar-chart", name: "Radar/Spider Chart", category: "Comparison & Part-to-whole", icon: Hexagon },
  { id: "pyramid", name: "Pyramid", category: "Comparison & Part-to-whole", icon: Triangle },

  // Distribution & Trends
  { id: "line-graph", name: "Line Graph", category: "Distribution & Trends", icon: TrendingUp },
  { id: "area-chart", name: "Area Chart", category: "Distribution & Trends", icon: Activity },
  { id: "streamgraph", name: "Streamgraph", category: "Distribution & Trends", icon: Waves },
  { id: "histogram", name: "Histogram", category: "Distribution & Trends", icon: BarChart3 },
  { id: "box-plot", name: "Box Plot", category: "Distribution & Trends", icon: Box },
  { id: "violin-plot", name: "Violin Plot", category: "Distribution & Trends", icon: Music },
  { id: "ridge-plot", name: "Ridge/Joy Plot", category: "Distribution & Trends", icon: Mountain },
  { id: "scatter-plot", name: "Scatter Plot", category: "Distribution & Trends", icon: Circle },
  { id: "bubble-chart", name: "Bubble Chart", category: "Distribution & Trends", icon: Circle },
  { id: "hexbin", name: "Hexbin", category: "Distribution & Trends", icon: Hexagon },
  { id: "density-contour", name: "Density Contour", category: "Distribution & Trends", icon: MapPin },
  { id: "control-chart", name: "Control Chart", category: "Distribution & Trends", icon: BarChart3 },
  { id: "bump-chart", name: "Bump Chart", category: "Distribution & Trends", icon: TrendingUp },
  { id: "small-multiples", name: "Small Multiples", category: "Distribution & Trends", icon: Grid3x3 },
  { id: "calendar-heatmap", name: "Calendar Heatmap", category: "Distribution & Trends", icon: Calendar },
  { id: "matrix-heatmap", name: "Matrix Heatmap", category: "Distribution & Trends", icon: Flame },

  // Networks & Systems
  { id: "network-diagram", name: "Network Diagram", category: "Networks & Systems", icon: Network },
  { id: "chord-diagram", name: "Chord Diagram", category: "Networks & Systems", icon: Music },
  { id: "arc-diagram", name: "Arc Diagram", category: "Networks & Systems", icon: Rainbow },
  { id: "parallel-coordinates", name: "Parallel Coordinates", category: "Networks & Systems", icon: Activity },
  { id: "hive-plot", name: "Hive Plot", category: "Networks & Systems", icon: Hexagon },

  // Simple layouts & docs
  { id: "cards-panels", name: "Cards/Panels", category: "Simple layouts & docs", icon: Square },
  { id: "iconic-infographic", name: "Iconic Infographic", category: "Simple layouts & docs", icon: BarChart3 },
  { id: "checklist", name: "Checklist", category: "Simple layouts & docs", icon: CheckSquare },
  { id: "numbered-list", name: "Numbered List", category: "Simple layouts & docs", icon: BookOpen },
  { id: "scorecard", name: "Scorecard/KPI", category: "Simple layouts & docs", icon: Trophy },
  { id: "risk-matrix", name: "Risk Matrix", category: "Simple layouts & docs", icon: AlertTriangle },
  { id: "priority-matrix", name: "Priority Matrix", category: "Simple layouts & docs", icon: Target },
  { id: "moscow-matrix", name: "MoSCoW Matrix", category: "Simple layouts & docs", icon: Target },
  { id: "raci-matrix", name: "RACI Matrix", category: "Simple layouts & docs", icon: Users },
  { id: "storyboard", name: "Storyboard", category: "Simple layouts & docs", icon: BookOpen }
];

const categories = [...new Set(visualTypes.map(v => v.category))];

const getPreviewSvg = (type: string) => {
  switch (type) {
    // Structure & Relationships
    case "mindmap":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="6" fill="#f59e0b" stroke="#d97706" strokeWidth="1"/>
            <circle cx="18" cy="14" r="4" fill="#3b82f6" stroke="#2563eb" strokeWidth="1"/>
            <circle cx="46" cy="14" r="4" fill="#10b981" stroke="#059669" strokeWidth="1"/>
            <circle cx="18" cy="34" r="4" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="1"/>
            <circle cx="46" cy="34" r="4" fill="#ef4444" stroke="#dc2626" strokeWidth="1"/>
            <line x1="32" y1="24" x2="18" y2="14" stroke="#6b7280" strokeWidth="2"/>
            <line x1="32" y1="24" x2="46" y2="14" stroke="#6b7280" strokeWidth="2"/>
            <line x1="32" y1="24" x2="18" y2="34" stroke="#6b7280" strokeWidth="2"/>
            <line x1="32" y1="24" x2="46" y2="34" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "org-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="24" y="4" width="16" height="8" rx="2" fill="#6366f1" stroke="#4f46e5" strokeWidth="1"/>
            <rect x="8" y="20" width="12" height="6" rx="1" fill="#818cf8" stroke="#6366f1" strokeWidth="1"/>
            <rect x="44" y="20" width="12" height="6" rx="1" fill="#818cf8" stroke="#6366f1" strokeWidth="1"/>
            <rect x="26" y="35" width="12" height="6" rx="1" fill="#a5b4fc" stroke="#818cf8" strokeWidth="1"/>
            <line x1="32" y1="12" x2="32" y2="15" stroke="#6b7280" strokeWidth="1"/>
            <line x1="20" y1="15" x2="44" y2="15" stroke="#6b7280" strokeWidth="1"/>
            <line x1="20" y1="15" x2="20" y2="20" stroke="#6b7280" strokeWidth="1"/>
            <line x1="44" y1="15" x2="44" y2="20" stroke="#6b7280" strokeWidth="1"/>
            <line x1="32" y1="26" x2="32" y2="35" stroke="#6b7280" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "hierarchy":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="8" r="4" fill="#16a34a"/>
            <circle cx="20" cy="20" r="3" fill="#22c55e"/>
            <circle cx="44" cy="20" r="3" fill="#22c55e"/>
            <circle cx="12" cy="32" r="2" fill="#4ade80"/>
            <circle cx="20" cy="32" r="2" fill="#4ade80"/>
            <circle cx="44" cy="32" r="2" fill="#4ade80"/>
            <circle cx="52" cy="32" r="2" fill="#4ade80"/>
            <line x1="32" y1="12" x2="20" y2="17" stroke="#6b7280"/>
            <line x1="32" y1="12" x2="44" y2="17" stroke="#6b7280"/>
            <line x1="20" y1="23" x2="12" y2="30" stroke="#6b7280"/>
            <line x1="20" y1="23" x2="20" y2="30" stroke="#6b7280"/>
            <line x1="44" y1="23" x2="44" y2="30" stroke="#6b7280"/>
            <line x1="44" y1="23" x2="52" y2="30" stroke="#6b7280"/>
          </svg>
        </div>
      );
    case "radial-wheel":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="18" fill="none" stroke="#8b5cf6" strokeWidth="2"/>
            <circle cx="32" cy="24" r="3" fill="#8b5cf6"/>
            <circle cx="32" cy="6" r="2" fill="#a78bfa"/>
            <circle cx="50" cy="24" r="2" fill="#a78bfa"/>
            <circle cx="32" cy="42" r="2" fill="#a78bfa"/>
            <circle cx="14" cy="24" r="2" fill="#a78bfa"/>
            <circle cx="44" cy="12" r="2" fill="#c4b5fd"/>
            <circle cx="44" cy="36" r="2" fill="#c4b5fd"/>
            <circle cx="20" cy="36" r="2" fill="#c4b5fd"/>
            <circle cx="20" cy="12" r="2" fill="#c4b5fd"/>
            <line x1="32" y1="24" x2="32" y2="6" stroke="#8b5cf6"/>
            <line x1="32" y1="24" x2="50" y2="24" stroke="#8b5cf6"/>
            <line x1="32" y1="24" x2="32" y2="42" stroke="#8b5cf6"/>
            <line x1="32" y1="24" x2="14" y2="24" stroke="#8b5cf6"/>
            <line x1="32" y1="24" x2="44" y2="12" stroke="#a78bfa"/>
            <line x1="32" y1="24" x2="44" y2="36" stroke="#a78bfa"/>
            <line x1="32" y1="24" x2="20" y2="36" stroke="#a78bfa"/>
            <line x1="32" y1="24" x2="20" y2="12" stroke="#a78bfa"/>
          </svg>
        </div>
      );
    case "circle-packing":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="20" fill="rgba(6, 182, 212, 0.3)" stroke="#06b6d4"/>
            <circle cx="24" cy="20" r="8" fill="rgba(34, 211, 238, 0.5)" stroke="#22d3ee"/>
            <circle cx="40" cy="28" r="6" fill="rgba(103, 232, 249, 0.5)" stroke="#67e8f9"/>
            <circle cx="28" cy="32" r="4" fill="rgba(165, 243, 252, 0.7)" stroke="#a5f3fc"/>
            <circle cx="36" cy="18" r="3" fill="rgba(207, 250, 254, 0.7)" stroke="#cffafe"/>
          </svg>
        </div>
      );
    case "treemap":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" fill="none" stroke="#059669" strokeWidth="2"/>
            <rect x="8" y="8" width="24" height="20" fill="#10b981" opacity="0.7"/>
            <rect x="32" y="8" width="24" height="12" fill="#34d399" opacity="0.7"/>
            <rect x="32" y="20" width="24" height="8" fill="#6ee7b7" opacity="0.7"/>
            <rect x="8" y="28" width="15" height="12" fill="#a7f3d0" opacity="0.7"/>
            <rect x="23" y="28" width="9" height="12" fill="#d1fae5" opacity="0.7"/>
            <rect x="32" y="28" width="24" height="12" fill="#ecfdf5" opacity="0.7"/>
          </svg>
        </div>
      );
    case "sunburst":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="6" fill="#f59e0b"/>
            <path d="M32,24 L32,12 A12,12 0 0,1 44,24 Z" fill="#fbbf24" opacity="0.8"/>
            <path d="M32,24 L44,24 A12,12 0 0,1 32,36 Z" fill="#fcd34d" opacity="0.8"/>
            <path d="M32,24 L32,36 A12,12 0 0,1 20,24 Z" fill="#fde68a" opacity="0.8"/>
            <path d="M32,24 L20,24 A12,12 0 0,1 32,12 Z" fill="#fef3c7" opacity="0.8"/>
            <path d="M32,24 L32,6 A18,18 0 0,1 50,24 Z" fill="#f59e0b" opacity="0.5"/>
            <path d="M32,24 L50,24 A18,18 0 0,1 32,42 Z" fill="#fbbf24" opacity="0.5"/>
            <path d="M32,24 L32,42 A18,18 0 0,1 14,24 Z" fill="#fcd34d" opacity="0.5"/>
            <path d="M32,24 L14,24 A18,18 0 0,1 32,6 Z" fill="#fde68a" opacity="0.5"/>
          </svg>
        </div>
      );
    case "icicle":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="8" fill="#3b82f6"/>
            <rect x="8" y="16" width="20" height="6" fill="#60a5fa"/>
            <rect x="28" y="16" width="28" height="6" fill="#60a5fa"/>
            <rect x="8" y="22" width="8" height="5" fill="#93c5fd"/>
            <rect x="16" y="22" width="12" height="5" fill="#93c5fd"/>
            <rect x="28" y="22" width="15" height="5" fill="#93c5fd"/>
            <rect x="43" y="22" width="13" height="5" fill="#93c5fd"/>
          </svg>
        </div>
      );

    // Process & Flow
    case "flowchart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="6" width="16" height="10" rx="2" fill="#3b82f6" stroke="#2563eb" strokeWidth="1"/>
            <polygon points="32,10 42,16 32,22 22,16" fill="#60a5fa" stroke="#3b82f6" strokeWidth="1"/>
            <rect x="48" y="6" width="12" height="10" rx="2" fill="#93c5fd" stroke="#60a5fa" strokeWidth="1"/>
            <rect x="8" y="30" width="16" height="10" rx="2" fill="#3b82f6" stroke="#2563eb" strokeWidth="1"/>
            <rect x="40" y="30" width="16" height="10" rx="2" fill="#93c5fd" stroke="#60a5fa" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "process-diagram":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="6" y="18" width="12" height="12" rx="2" fill="#6366f1"/>
            <rect x="26" y="18" width="12" height="12" rx="2" fill="#818cf8"/>
            <rect x="46" y="18" width="12" height="12" rx="2" fill="#a5b4fc"/>
            <polygon points="18,24 24,24 21,27" fill="#4f46e5"/>
            <polygon points="38,24 44,24 41,27" fill="#6366f1"/>
          </svg>
        </div>
      );
    case "swimlane":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="8" y1="16" x2="56" y2="16" stroke="#14b8a6" strokeWidth="1"/>
            <line x1="8" y1="32" x2="56" y2="32" stroke="#14b8a6" strokeWidth="1"/>
            <rect x="12" y="8" width="12" height="6" fill="#0d9488"/>
            <rect x="32" y="20" width="12" height="6" fill="#14b8a6"/>
            <rect x="48" y="36" width="8" height="6" fill="#2dd4bf"/>
            <polygon points="24,11 30,11 27,14" fill="#0f766e"/>
            <polygon points="44,23 50,23 47,26" fill="#0d9488"/>
          </svg>
        </div>
      );
    case "bpmn":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="16" cy="24" r="6" fill="#64748b"/>
            <rect x="28" y="18" width="12" height="12" rx="1" fill="#94a3b8"/>
            <polygon points="48,18 58,24 48,30 46,24" fill="#cbd5e1"/>
            <circle cx="16" cy="24" r="3" fill="white"/>
            <line x1="22" y1="24" x2="28" y2="24" stroke="#374151" strokeWidth="2"/>
            <line x1="40" y1="24" x2="46" y2="24" stroke="#374151" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "state-machine":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-violet-100 to-violet-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="16" cy="24" r="8" fill="#8b5cf6" stroke="#7c3aed" strokeWidth="2"/>
            <circle cx="48" cy="24" r="8" fill="#a78bfa" stroke="#8b5cf6" strokeWidth="2"/>
            <path d="M24,20 Q32,16 40,20" fill="none" stroke="#6d28d9" strokeWidth="2" markerEnd="url(#arrow)"/>
            <path d="M40,28 Q32,32 24,28" fill="none" stroke="#6d28d9" strokeWidth="2" markerEnd="url(#arrow)"/>
          </svg>
        </div>
      );
    case "decision-tree":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="26" y="6" width="12" height="8" rx="2" fill="#16a34a"/>
            <circle cx="16" cy="28" r="4" fill="#22c55e"/>
            <circle cx="48" cy="28" r="4" fill="#22c55e"/>
            <rect x="10" y="38" width="8" height="6" rx="1" fill="#4ade80"/>
            <rect x="44" y="38" width="8" height="6" rx="1" fill="#4ade80"/>
            <line x1="32" y1="14" x2="20" y2="24" stroke="#166534"/>
            <line x1="32" y1="14" x2="44" y2="24" stroke="#166534"/>
            <line x1="16" y1="32" x2="14" y2="38" stroke="#166534"/>
            <line x1="48" y1="32" x2="48" y2="38" stroke="#166534"/>
          </svg>
        </div>
      );
    case "customer-journey":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-rose-100 to-rose-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <path d="M8,36 Q16,12 24,20 T40,16 T56,24" fill="none" stroke="#f43f5e" strokeWidth="3"/>
            <circle cx="12" cy="32" r="3" fill="#e11d48"/>
            <circle cx="24" cy="20" r="3" fill="#f43f5e"/>
            <circle cx="40" cy="16" r="3" fill="#fb7185"/>
            <circle cx="52" cy="24" r="3" fill="#fda4af"/>
            <rect x="8" y="6" width="4" height="4" fill="#be123c"/>
            <rect x="20" y="8" width="4" height="4" fill="#e11d48"/>
            <rect x="36" y="4" width="4" height="4" fill="#f43f5e"/>
            <rect x="48" y="12" width="4" height="4" fill="#fb7185"/>
          </svg>
        </div>
      );
    case "service-blueprint":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="8" y1="12" x2="56" y2="12" stroke="#d97706" strokeWidth="2"/>
            <line x1="8" y1="24" x2="56" y2="24" stroke="#f59e0b" strokeWidth="1" strokeDasharray="4,2"/>
            <line x1="8" y1="36" x2="56" y2="36" stroke="#fbbf24" strokeWidth="1"/>
            <rect x="12" y="4" width="8" height="6" fill="#92400e"/>
            <rect x="32" y="4" width="8" height="6" fill="#b45309"/>
            <circle cx="16" cy="18" r="3" fill="#d97706"/>
            <circle cx="36" cy="18" r="3" fill="#f59e0b"/>
            <rect x="12" y="28" width="8" height="6" fill="#fbbf24"/>
            <rect x="32" y="28" width="8" height="6" fill="#fcd34d"/>
          </svg>
        </div>
      );
    case "roadmap":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="8" y1="24" x2="56" y2="24" stroke="#2563eb" strokeWidth="3"/>
            <rect x="10" y="12" width="8" height="8" fill="#3b82f6"/>
            <rect x="24" y="16" width="8" height="8" fill="#60a5fa"/>
            <rect x="38" y="14" width="8" height="8" fill="#93c5fd"/>
            <rect x="52" y="18" width="8" height="8" fill="#dbeafe"/>
            <text x="14" y="36" fontSize="6" fill="#1e40af">Q1</text>
            <text x="28" y="36" fontSize="6" fill="#1e40af">Q2</text>
            <text x="42" y="36" fontSize="6" fill="#1e40af">Q3</text>
            <text x="56" y="36" fontSize="6" fill="#1e40af">Q4</text>
          </svg>
        </div>
      );
    case "timeline":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="8" y1="24" x2="56" y2="24" stroke="#14b8a6" strokeWidth="3"/>
            <circle cx="16" cy="24" r="3" fill="#0d9488"/>
            <circle cx="32" cy="24" r="3" fill="#14b8a6"/>
            <circle cx="48" cy="24" r="3" fill="#2dd4bf"/>
            <rect x="12" y="8" width="8" height="6" rx="1" fill="#5eead4"/>
            <rect x="28" y="32" width="8" height="6" rx="1" fill="#5eead4"/>
            <rect x="44" y="8" width="8" height="6" rx="1" fill="#5eead4"/>
          </svg>
        </div>
      );
    case "spiral-timeline":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <path d="M32,24 Q44,12 32,12 Q20,12 20,24 Q20,36 32,36 Q44,36 44,24 Q44,16 38,16" 
                  fill="none" stroke="#8b5cf6" strokeWidth="2"/>
            <circle cx="32" cy="24" r="2" fill="#7c3aed"/>
            <circle cx="38" cy="16" r="2" fill="#a78bfa"/>
            <circle cx="26" cy="18" r="2" fill="#c4b5fd"/>
            <circle cx="38" cy="30" r="2" fill="#ddd6fe"/>
          </svg>
        </div>
      );
    case "cycle-diagram":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="16" fill="none" stroke="#f97316" strokeWidth="2"/>
            <circle cx="32" cy="8" r="4" fill="#ea580c"/>
            <circle cx="48" cy="24" r="4" fill="#f97316"/>
            <circle cx="32" cy="40" r="4" fill="#fb923c"/>
            <circle cx="16" cy="24" r="4" fill="#fed7aa"/>
            <path d="M32,12 Q40,16 44,24" fill="none" stroke="#ea580c" strokeWidth="2" markerEnd="url(#arrow)"/>
          </svg>
        </div>
      );
    case "funnel":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <polygon points="8,8 56,8 52,16 12,16" fill="#10b981"/>
            <polygon points="12,18 52,18 48,26 16,26" fill="#34d399"/>
            <polygon points="16,28 48,28 44,36 20,36" fill="#6ee7b7"/>
            <polygon points="20,38 44,38 40,44 24,44" fill="#a7f3d0"/>
          </svg>
        </div>
      );
    case "alluvial":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <path d="M8,12 Q32,12 56,16" fill="none" stroke="#06b6d4" strokeWidth="6" opacity="0.7"/>
            <path d="M8,24 Q32,24 56,24" fill="none" stroke="#22d3ee" strokeWidth="4" opacity="0.7"/>
            <path d="M8,36 Q32,36 56,32" fill="none" stroke="#67e8f9" strokeWidth="8" opacity="0.7"/>
            <line x1="8" y1="8" x2="8" y2="40" stroke="#0891b2" strokeWidth="3"/>
            <line x1="56" y1="12" x2="56" y2="36" stroke="#0891b2" strokeWidth="3"/>
          </svg>
        </div>
      );

    // Comparison & Part-to-whole
    case "table":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" fill="none" stroke="#64748b" strokeWidth="2"/>
            <line x1="8" y1="18" x2="56" y2="18" stroke="#64748b" strokeWidth="1"/>
            <line x1="8" y1="28" x2="56" y2="28" stroke="#64748b" strokeWidth="1"/>
            <line x1="24" y1="8" x2="24" y2="40" stroke="#64748b" strokeWidth="1"/>
            <line x1="40" y1="8" x2="40" y2="40" stroke="#64748b" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "comparison-table":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" fill="none" stroke="#64748b" strokeWidth="2"/>
            <rect x="8" y="8" width="48" height="8" fill="#94a3b8"/>
            <line x1="8" y1="24" x2="56" y2="24" stroke="#64748b" strokeWidth="1"/>
            <line x1="8" y1="32" x2="56" y2="32" stroke="#64748b" strokeWidth="1"/>
            <line x1="32" y1="8" x2="32" y2="40" stroke="#64748b" strokeWidth="2"/>
            <circle cx="20" cy="28" r="2" fill="#22c55e"/>
            <path d="M40,26 L42,28 L46,24" stroke="#ef4444" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      );
    case "pros-cons":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-rose-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="32" y1="8" x2="32" y2="40" stroke="#6b7280" strokeWidth="2"/>
            <rect x="8" y="8" width="24" height="32" fill="#dcfce7" opacity="0.5"/>
            <rect x="32" y="8" width="24" height="32" fill="#fecaca" opacity="0.5"/>
            <circle cx="16" cy="18" r="2" fill="#22c55e"/>
            <circle cx="16" cy="26" r="2" fill="#22c55e"/>
            <circle cx="16" cy="34" r="2" fill="#22c55e"/>
            <path d="M40,16 L42,18 L46,14" stroke="#ef4444" strokeWidth="2" fill="none"/>
            <path d="M40,24 L42,26 L46,22" stroke="#ef4444" strokeWidth="2" fill="none"/>
            <path d="M40,32 L42,34 L46,30" stroke="#ef4444" strokeWidth="2" fill="none"/>
          </svg>
        </div>
      );
    case "bar-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="10" y="25" width="8" height="15" fill="#8b5cf6"/>
            <rect x="22" y="20" width="8" height="20" fill="#a78bfa"/>
            <rect x="34" y="15" width="8" height="25" fill="#8b5cf6"/>
            <rect x="46" y="18" width="8" height="22" fill="#a78bfa"/>
            <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
            <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "lollipop-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-pink-100 to-pink-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="14" y1="35" x2="14" y2="25" stroke="#ec4899" strokeWidth="2"/>
            <line x1="26" y1="35" x2="26" y2="20" stroke="#ec4899" strokeWidth="2"/>
            <line x1="38" y1="35" x2="38" y2="15" stroke="#ec4899" strokeWidth="2"/>
            <line x1="50" y1="35" x2="50" y2="18" stroke="#ec4899" strokeWidth="2"/>
            <circle cx="14" cy="25" r="3" fill="#f472b6"/>
            <circle cx="26" cy="20" r="3" fill="#f472b6"/>
            <circle cx="38" cy="15" r="3" fill="#f472b6"/>
            <circle cx="50" cy="18" r="3" fill="#f472b6"/>
            <line x1="8" y1="35" x2="56" y2="35" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "dot-plot":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="16" cy="15" r="2" fill="#6366f1"/>
            <circle cx="24" cy="25" r="2" fill="#818cf8"/>
            <circle cx="32" cy="18" r="2" fill="#6366f1"/>
            <circle cx="40" cy="30" r="2" fill="#a5b4fc"/>
            <circle cx="48" cy="20" r="2" fill="#818cf8"/>
            <line x1="6" y1="35" x2="58" y2="35" stroke="#6b7280" strokeWidth="2"/>
            <line x1="6" y1="10" x2="6" y2="35" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "dumbbell-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="12" y1="15" x2="28" y2="15" stroke="#6b7280" strokeWidth="3"/>
            <line x1="16" y1="25" x2="32" y2="25" stroke="#6b7280" strokeWidth="3"/>
            <line x1="14" y1="35" x2="36" y2="35" stroke="#6b7280" strokeWidth="3"/>
            <circle cx="12" cy="15" r="3" fill="#374151"/>
            <circle cx="28" cy="15" r="3" fill="#9ca3af"/>
            <circle cx="16" cy="25" r="3" fill="#374151"/>
            <circle cx="32" cy="25" r="3" fill="#9ca3af"/>
            <circle cx="14" cy="35" r="3" fill="#374151"/>
            <circle cx="36" cy="35" r="3" fill="#9ca3af"/>
          </svg>
        </div>
      );
    case "slopegraph":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="16" y1="12" x2="48" y2="18" stroke="#16a34a" strokeWidth="2"/>
            <line x1="16" y1="20" x2="48" y2="25" stroke="#22c55e" strokeWidth="2"/>
            <line x1="16" y1="28" x2="48" y2="15" stroke="#4ade80" strokeWidth="2"/>
            <line x1="16" y1="36" x2="48" y2="32" stroke="#86efac" strokeWidth="2"/>
            <circle cx="16" cy="12" r="2" fill="#15803d"/>
            <circle cx="16" cy="20" r="2" fill="#16a34a"/>
            <circle cx="16" cy="28" r="2" fill="#22c55e"/>
            <circle cx="16" cy="36" r="2" fill="#4ade80"/>
            <circle cx="48" cy="18" r="2" fill="#15803d"/>
            <circle cx="48" cy="25" r="2" fill="#16a34a"/>
            <circle cx="48" cy="15" r="2" fill="#22c55e"/>
            <circle cx="48" cy="32" r="2" fill="#4ade80"/>
          </svg>
        </div>
      );
    case "bullet-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="18" width="40" height="12" fill="#e5e7eb"/>
            <rect x="8" y="20" width="32" height="8" fill="#9ca3af"/>
            <rect x="8" y="22" width="24" height="4" fill="#374151"/>
            <line x1="30" y1="16" x2="30" y2="32" stroke="#ef4444" strokeWidth="3"/>
          </svg>
        </div>
      );
    case "waterfall-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-sky-100 to-sky-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="20" width="8" height="20" fill="#0ea5e9"/>
            <rect x="20" y="15" width="8" height="5" fill="#38bdf8"/>
            <rect x="32" y="18" width="8" height="3" fill="#7dd3fc"/>
            <rect x="44" y="25" width="8" height="15" fill="#0ea5e9"/>
            <line x1="16" y1="20" x2="20" y2="20" stroke="#6b7280" strokeWidth="1" strokeDasharray="2,2"/>
            <line x1="28" y1="15" x2="32" y2="18" stroke="#6b7280" strokeWidth="1" strokeDasharray="2,2"/>
            <line x1="40" y1="18" x2="44" y2="25" stroke="#6b7280" strokeWidth="1" strokeDasharray="2,2"/>
            <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "marimekko":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="15" height="32" fill="#8b5cf6"/>
            <rect x="8" y="8" width="15" height="16" fill="#a78bfa"/>
            <rect x="23" y="8" width="18" height="32" fill="#c4b5fd"/>
            <rect x="23" y="8" width="18" height="20" fill="#ddd6fe"/>
            <rect x="41" y="8" width="15" height="32" fill="#ede9fe"/>
            <rect x="41" y="8" width="15" height="12" fill="#f3f4f6"/>
          </svg>
        </div>
      );
    case "waffle-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="10" y="12" width="6" height="6" fill="#f59e0b"/>
            <rect x="18" y="12" width="6" height="6" fill="#f59e0b"/>
            <rect x="26" y="12" width="6" height="6" fill="#fbbf24"/>
            <rect x="34" y="12" width="6" height="6" fill="#fbbf24"/>
            <rect x="42" y="12" width="6" height="6" fill="#fcd34d"/>
            <rect x="10" y="20" width="6" height="6" fill="#f59e0b"/>
            <rect x="18" y="20" width="6" height="6" fill="#fbbf24"/>
            <rect x="26" y="20" width="6" height="6" fill="#fbbf24"/>
            <rect x="34" y="20" width="6" height="6" fill="#fcd34d"/>
            <rect x="42" y="20" width="6" height="6" fill="#fde68a"/>
            <rect x="10" y="28" width="6" height="6" fill="#fbbf24"/>
            <rect x="18" y="28" width="6" height="6" fill="#fcd34d"/>
            <rect x="26" y="28" width="6" height="6" fill="#fde68a"/>
            <rect x="34" y="28" width="6" height="6" fill="#fef3c7"/>
            <rect x="42" y="28" width="6" height="6" fill="#fefbf0"/>
          </svg>
        </div>
      );
    case "pie-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="18" fill="#10b981" stroke="#059669" strokeWidth="1"/>
            <path d="M32,24 L32,6 A18,18 0 0,1 46.36,14.5 Z" fill="#34d399" stroke="#10b981" strokeWidth="1"/>
            <path d="M32,24 L46.36,14.5 A18,18 0 0,1 46.36,33.5 Z" fill="#6ee7b7" stroke="#34d399" strokeWidth="1"/>
            <path d="M32,24 L46.36,33.5 A18,18 0 0,1 32,42 Z" fill="#a7f3d0" stroke="#6ee7b7" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "donut-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="16" fill="#3b82f6"/>
            <circle cx="32" cy="24" r="8" fill="white"/>
            <path d="M32,8 A16,16 0 0,1 48,24 L40,24 A8,8 0 0,0 32,16 Z" fill="#60a5fa"/>
            <path d="M48,24 A16,16 0 0,1 32,40 L32,32 A8,8 0 0,0 40,24 Z" fill="#93c5fd"/>
            <path d="M32,40 A16,16 0 0,1 16,24 L24,24 A8,8 0 0,0 32,32 Z" fill="#dbeafe"/>
          </svg>
        </div>
      );
    case "polar-area":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="18" fill="none" stroke="#14b8a6" strokeWidth="1"/>
            <path d="M32,24 L32,6 A18,18 0 0,1 44,12 Z" fill="rgba(20, 184, 166, 0.8)"/>
            <path d="M32,24 L44,12 A18,18 0 0,1 50,24 Z" fill="rgba(45, 212, 191, 0.6)"/>
            <path d="M32,24 L50,24 A18,18 0 0,1 44,36 Z" fill="rgba(94, 234, 212, 0.4)"/>
            <path d="M32,24 L44,36 A18,18 0 0,1 32,42 Z" fill="rgba(153, 246, 228, 0.6)"/>
            <path d="M32,24 L32,42 A18,18 0 0,1 20,36 Z" fill="rgba(204, 251, 241, 0.8)"/>
            <path d="M32,24 L20,36 A18,18 0 0,1 14,24 Z" fill="rgba(240, 253, 250, 0.4)"/>
            <path d="M32,24 L14,24 A18,18 0 0,1 20,12 Z" fill="rgba(20, 184, 166, 0.6)"/>
            <path d="M32,24 L20,12 A18,18 0 0,1 32,6 Z" fill="rgba(45, 212, 191, 0.8)"/>
          </svg>
        </div>
      );
    case "radar-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-violet-100 to-violet-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <polygon points="32,12 44,20 40,32 24,32 20,20" 
                     fill="rgba(139, 92, 246, 0.3)" stroke="#8b5cf6" strokeWidth="2"/>
            <line x1="32" y1="24" x2="32" y2="8" stroke="#6b7280" strokeWidth="1"/>
            <line x1="32" y1="24" x2="48" y2="18" stroke="#6b7280" strokeWidth="1"/>
            <line x1="32" y1="24" x2="48" y2="36" stroke="#6b7280" strokeWidth="1"/>
            <line x1="32" y1="24" x2="16" y2="36" stroke="#6b7280" strokeWidth="1"/>
            <line x1="32" y1="24" x2="16" y2="18" stroke="#6b7280" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "pyramid":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <polygon points="32,8 50,20 14,20" fill="#eab308"/>
            <polygon points="26,20 38,20 44,30 20,30" fill="#facc15"/>
            <polygon points="20,30 44,30 48,38 16,38" fill="#fde047"/>
            <polygon points="16,38 48,38 52,44 12,44" fill="#fef08a"/>
          </svg>
        </div>
      );

    // Distribution & Trends
    case "line-graph":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-red-100 to-red-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <polyline points="8,35 16,25 24,30 32,20 40,28 48,18 56,22" 
                      fill="none" stroke="#dc2626" strokeWidth="2"/>
            <circle cx="16" cy="25" r="2" fill="#ef4444"/>
            <circle cx="24" cy="30" r="2" fill="#f87171"/>
            <circle cx="32" cy="20" r="2" fill="#fca5a5"/>
            <circle cx="40" cy="28" r="2" fill="#ef4444"/>
            <circle cx="48" cy="18" r="2" fill="#dc2626"/>
            <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
            <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "area-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <path d="M8,35 L16,25 L24,30 L32,20 L40,28 L48,18 L56,22 L56,35 Z" 
                  fill="rgba(59, 130, 246, 0.3)" stroke="#3b82f6" strokeWidth="2"/>
            <line x1="6" y1="35" x2="58" y2="35" stroke="#6b7280" strokeWidth="2"/>
            <line x1="6" y1="8" x2="6" y2="35" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "streamgraph":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <path d="M8,24 Q16,16 24,20 T40,18 T56,22 Q56,28 40,30 T24,28 T8,30 Z" 
                  fill="rgba(6, 182, 212, 0.6)"/>
            <path d="M8,22 Q16,18 24,20 T40,16 T56,18 Q56,22 40,24 T24,22 T8,24 Z" 
                  fill="rgba(34, 211, 238, 0.6)"/>
            <path d="M8,26 Q16,24 24,26 T40,24 T56,26 Q56,30 40,32 T24,30 T8,32 Z" 
                  fill="rgba(103, 232, 249, 0.6)"/>
          </svg>
        </div>
      );
    case "histogram":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="25" width="6" height="15" fill="#f59e0b"/>
            <rect x="16" y="18" width="6" height="22" fill="#fbbf24"/>
            <rect x="24" y="20" width="6" height="20" fill="#f59e0b"/>
            <rect x="32" y="15" width="6" height="25" fill="#fbbf24"/>
            <rect x="40" y="22" width="6" height="18" fill="#f59e0b"/>
            <rect x="48" y="28" width="6" height="12" fill="#fbbf24"/>
            <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
            <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "box-plot":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-lime-100 to-lime-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="16" y="18" width="32" height="12" fill="none" stroke="#84cc16" strokeWidth="2"/>
            <line x1="24" y1="18" x2="24" y2="30" stroke="#84cc16" strokeWidth="2"/>
            <line x1="8" y1="24" x2="16" y2="24" stroke="#65a30d" strokeWidth="2"/>
            <line x1="48" y1="24" x2="56" y2="24" stroke="#65a30d" strokeWidth="2"/>
            <line x1="8" y1="20" x2="8" y2="28" stroke="#65a30d" strokeWidth="2"/>
            <line x1="56" y1="20" x2="56" y2="28" stroke="#65a30d" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "violin-plot":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-pink-100 to-pink-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <path d="M32,8 Q20,12 20,24 Q20,36 32,40 Q44,36 44,24 Q44,12 32,8 Z" 
                  fill="rgba(236, 72, 153, 0.3)" stroke="#ec4899" strokeWidth="2"/>
            <line x1="32" y1="12" x2="32" y2="36" stroke="#be185d" strokeWidth="2"/>
            <rect x="28" y="20" width="8" height="8" fill="none" stroke="#be185d" strokeWidth="1"/>
            <line x1="30" y1="24" x2="34" y2="24" stroke="#be185d" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "ridge-plot":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <path d="M8,15 Q20,10 32,15 Q44,20 56,15" fill="none" stroke="#6366f1" strokeWidth="2"/>
            <path d="M8,25 Q20,20 32,25 Q44,30 56,25" fill="none" stroke="#818cf8" strokeWidth="2"/>
            <path d="M8,35 Q20,30 32,35 Q44,40 56,35" fill="none" stroke="#a5b4fc" strokeWidth="2"/>
            <path d="M8,15 Q20,10 32,15 Q44,20 56,15 L56,15 L8,15 Z" 
                  fill="rgba(99, 102, 241, 0.2)"/>
            <path d="M8,25 Q20,20 32,25 Q44,30 56,25 L56,25 L8,25 Z" 
                  fill="rgba(129, 140, 248, 0.2)"/>
            <path d="M8,35 Q20,30 32,35 Q44,40 56,35 L56,35 L8,35 Z" 
                  fill="rgba(165, 180, 252, 0.2)"/>
          </svg>
        </div>
      );
    case "scatter-plot":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="14" cy="30" r="2" fill="#16a34a"/>
            <circle cx="18" cy="25" r="2" fill="#22c55e"/>
            <circle cx="26" cy="32" r="2" fill="#16a34a"/>
            <circle cx="30" cy="20" r="2" fill="#4ade80"/>
            <circle cx="36" cy="28" r="2" fill="#22c55e"/>
            <circle cx="42" cy="18" r="2" fill="#4ade80"/>
            <circle cx="48" cy="24" r="2" fill="#86efac"/>
            <circle cx="52" cy="16" r="2" fill="#bbf7d0"/>
            <circle cx="24" cy="18" r="2" fill="#fca5a5"/>
            <circle cx="38" cy="35" r="2" fill="#ef4444"/>
            <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
            <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "bubble-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-cyan-100 to-cyan-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="16" cy="30" r="4" fill="rgba(6, 182, 212, 0.6)" stroke="#06b6d4"/>
            <circle cx="32" cy="20" r="6" fill="rgba(34, 211, 238, 0.6)" stroke="#22d3ee"/>
            <circle cx="48" cy="28" r="5" fill="rgba(103, 232, 249, 0.6)" stroke="#67e8f9"/>
            <circle cx="24" cy="35" r="3" fill="rgba(6, 182, 212, 0.6)" stroke="#06b6d4"/>
            <line x1="6" y1="40" x2="58" y2="40" stroke="#6b7280" strokeWidth="2"/>
            <line x1="6" y1="8" x2="6" y2="40" stroke="#6b7280" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "hexbin":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <polygon points="16,12 20,8 28,8 32,12 28,16 20,16" fill="#8b5cf6" opacity="0.7"/>
            <polygon points="32,12 36,8 44,8 48,12 44,16 36,16" fill="#a78bfa" opacity="0.5"/>
            <polygon points="16,24 20,20 28,20 32,24 28,28 20,28" fill="#c4b5fd" opacity="0.6"/>
            <polygon points="32,24 36,20 44,20 48,24 44,28 36,28" fill="#ddd6fe" opacity="0.4"/>
            <polygon points="16,36 20,32 28,32 32,36 28,40 20,40" fill="#ede9fe" opacity="0.5"/>
            <polygon points="32,36 36,32 44,32 48,36 44,40 36,40" fill="#f5f3ff" opacity="0.3"/>
          </svg>
        </div>
      );
    case "density-contour":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-red-100 to-red-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <ellipse cx="32" cy="24" rx="20" ry="12" fill="none" stroke="#dc2626" strokeWidth="2"/>
            <ellipse cx="32" cy="24" rx="15" ry="9" fill="none" stroke="#ef4444" strokeWidth="2"/>
            <ellipse cx="32" cy="24" rx="10" ry="6" fill="none" stroke="#f87171" strokeWidth="2"/>
            <ellipse cx="32" cy="24" rx="5" ry="3" fill="#fca5a5"/>
          </svg>
        </div>
      );
    case "control-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="8" y1="16" x2="56" y2="16" stroke="#ea580c" strokeDasharray="4,2"/>
            <line x1="8" y1="24" x2="56" y2="24" stroke="#f97316" strokeWidth="2"/>
            <line x1="8" y1="32" x2="56" y2="32" stroke="#ea580c" strokeDasharray="4,2"/>
            <polyline points="8,26 16,22 24,28 32,25 40,30 48,23 56,27" 
                      fill="none" stroke="#fb923c" strokeWidth="2"/>
            <circle cx="16" cy="22" r="1.5" fill="#ea580c"/>
            <circle cx="24" cy="28" r="1.5" fill="#ea580c"/>
            <circle cx="32" cy="25" r="1.5" fill="#ea580c"/>
            <circle cx="40" cy="30" r="1.5" fill="#ea580c"/>
            <circle cx="48" cy="23" r="1.5" fill="#ea580c"/>
          </svg>
        </div>
      );
    case "bump-chart":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-teal-100 to-teal-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <polyline points="8,12 24,20 40,16 56,28" fill="none" stroke="#14b8a6" strokeWidth="2"/>
            <polyline points="8,20 24,12 40,24 56,16" fill="none" stroke="#2dd4bf" strokeWidth="2"/>
            <polyline points="8,28 24,32 40,20 56,24" fill="none" stroke="#5eead4" strokeWidth="2"/>
            <circle cx="8" cy="12" r="2" fill="#0f766e"/>
            <circle cx="24" cy="20" r="2" fill="#0f766e"/>
            <circle cx="40" cy="16" r="2" fill="#0f766e"/>
            <circle cx="56" cy="28" r="2" fill="#0f766e"/>
          </svg>
        </div>
      );
    case "small-multiples":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-gray-100 to-gray-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="6" y="6" width="24" height="16" fill="none" stroke="#6b7280" strokeWidth="1"/>
            <rect x="34" y="6" width="24" height="16" fill="none" stroke="#6b7280" strokeWidth="1"/>
            <rect x="6" y="26" width="24" height="16" fill="none" stroke="#6b7280" strokeWidth="1"/>
            <rect x="34" y="26" width="24" height="16" fill="none" stroke="#6b7280" strokeWidth="1"/>
            <polyline points="8,18 15,12 22,16 28,10" fill="none" stroke="#3b82f6" strokeWidth="1"/>
            <polyline points="36,18 43,14 50,12 56,16" fill="none" stroke="#ef4444" strokeWidth="1"/>
            <polyline points="8,38 15,32 22,36 28,30" fill="none" stroke="#16a34a" strokeWidth="1"/>
            <polyline points="36,38 43,34 50,32 56,36" fill="none" stroke="#f59e0b" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "calendar-heatmap":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="6" height="6" fill="#dcfce7"/>
            <rect x="16" y="8" width="6" height="6" fill="#bbf7d0"/>
            <rect x="24" y="8" width="6" height="6" fill="#86efac"/>
            <rect x="32" y="8" width="6" height="6" fill="#4ade80"/>
            <rect x="40" y="8" width="6" height="6" fill="#22c55e"/>
            <rect x="48" y="8" width="6" height="6" fill="#16a34a"/>
            <rect x="8" y="16" width="6" height="6" fill="#15803d"/>
            <rect x="16" y="16" width="6" height="6" fill="#22c55e"/>
            <rect x="24" y="16" width="6" height="6" fill="#4ade80"/>
            <rect x="32" y="16" width="6" height="6" fill="#86efac"/>
            <rect x="40" y="16" width="6" height="6" fill="#bbf7d0"/>
            <rect x="48" y="16" width="6" height="6" fill="#dcfce7"/>
            <rect x="8" y="24" width="6" height="6" fill="#4ade80"/>
            <rect x="16" y="24" width="6" height="6" fill="#16a34a"/>
            <rect x="24" y="24" width="6" height="6" fill="#15803d"/>
            <rect x="32" y="24" width="6" height="6" fill="#22c55e"/>
            <rect x="40" y="24" width="6" height="6" fill="#86efac"/>
            <rect x="48" y="24" width="6" height="6" fill="#dcfce7"/>
          </svg>
        </div>
      );
    case "matrix-heatmap":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-red-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="8" height="8" fill="#dc2626"/>
            <rect x="16" y="8" width="8" height="8" fill="#ef4444"/>
            <rect x="24" y="8" width="8" height="8" fill="#f87171"/>
            <rect x="32" y="8" width="8" height="8" fill="#fca5a5"/>
            <rect x="40" y="8" width="8" height="8" fill="#fed7d7"/>
            <rect x="48" y="8" width="8" height="8" fill="#fef2f2"/>
            <rect x="8" y="16" width="8" height="8" fill="#f97316"/>
            <rect x="16" y="16" width="8" height="8" fill="#fb923c"/>
            <rect x="24" y="16" width="8" height="8" fill="#fdba74"/>
            <rect x="32" y="16" width="8" height="8" fill="#fed7aa"/>
            <rect x="40" y="16" width="8" height="8" fill="#fef3c7"/>
            <rect x="48" y="16" width="8" height="8" fill="#fffbeb"/>
            <rect x="8" y="24" width="8" height="8" fill="#eab308"/>
            <rect x="16" y="24" width="8" height="8" fill="#facc15"/>
            <rect x="24" y="24" width="8" height="8" fill="#fde047"/>
            <rect x="32" y="24" width="8" height="8" fill="#fef08a"/>
            <rect x="40" y="24" width="8" height="8" fill="#fefce8"/>
            <rect x="48" y="24" width="8" height="8" fill="#fffef7"/>
          </svg>
        </div>
      );

    // Networks & Systems
    case "network-diagram":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-rose-100 to-rose-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="16" cy="12" r="4" fill="#f43f5e" stroke="#e11d48" strokeWidth="1"/>
            <circle cx="48" cy="12" r="4" fill="#fb7185" stroke="#f43f5e" strokeWidth="1"/>
            <circle cx="32" cy="24" r="4" fill="#fda4af" stroke="#fb7185" strokeWidth="1"/>
            <circle cx="16" cy="36" r="4" fill="#f43f5e" stroke="#e11d48" strokeWidth="1"/>
            <circle cx="48" cy="36" r="4" fill="#fb7185" stroke="#f43f5e" strokeWidth="1"/>
            <line x1="16" y1="12" x2="32" y2="24" stroke="#6b7280" strokeWidth="2"/>
            <line x1="48" y1="12" x2="32" y2="24" stroke="#6b7280" strokeWidth="2"/>
            <line x1="32" y1="24" x2="16" y2="36" stroke="#6b7280" strokeWidth="2"/>
            <line x1="32" y1="24" x2="48" y2="36" stroke="#6b7280" strokeWidth="2"/>
            <line x1="16" y1="12" x2="48" y2="36" stroke="#6b7280" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "chord-diagram":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="32" cy="24" r="16" fill="none" stroke="#8b5cf6" strokeWidth="2"/>
            <path d="M32,8 Q48,24 32,40" fill="none" stroke="#a78bfa" strokeWidth="3"/>
            <path d="M16,24 Q32,8 48,24" fill="none" stroke="#c4b5fd" strokeWidth="2"/>
            <path d="M32,40 Q16,24 32,8" fill="none" stroke="#ddd6fe" strokeWidth="2"/>
            <circle cx="32" cy="8" r="2" fill="#7c3aed"/>
            <circle cx="48" cy="24" r="2" fill="#8b5cf6"/>
            <circle cx="32" cy="40" r="2" fill="#a78bfa"/>
            <circle cx="16" cy="24" r="2" fill="#c4b5fd"/>
          </svg>
        </div>
      );
    case "arc-diagram":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="8" y1="32" x2="56" y2="32" stroke="#3b82f6" strokeWidth="2"/>
            <circle cx="12" cy="32" r="2" fill="#1e40af"/>
            <circle cx="24" cy="32" r="2" fill="#2563eb"/>
            <circle cx="36" cy="32" r="2" fill="#3b82f6"/>
            <circle cx="48" cy="32" r="2" fill="#60a5fa"/>
            <path d="M12,32 Q18,16 24,32" fill="none" stroke="#3b82f6" strokeWidth="2"/>
            <path d="M24,32 Q30,20 36,32" fill="none" stroke="#60a5fa" strokeWidth="2"/>
            <path d="M36,32 Q42,12 48,32" fill="none" stroke="#93c5fd" strokeWidth="2"/>
            <path d="M12,32 Q32,8 48,32" fill="none" stroke="#dbeafe" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "parallel-coordinates":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-indigo-100 to-indigo-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="12" y1="8" x2="12" y2="40" stroke="#6366f1" strokeWidth="2"/>
            <line x1="24" y1="8" x2="24" y2="40" stroke="#6366f1" strokeWidth="2"/>
            <line x1="36" y1="8" x2="36" y2="40" stroke="#6366f1" strokeWidth="2"/>
            <line x1="48" y1="8" x2="48" y2="40" stroke="#6366f1" strokeWidth="2"/>
            <polyline points="12,15 24,20 36,12 48,18" fill="none" stroke="#818cf8" strokeWidth="2"/>
            <polyline points="12,25 24,12 36,28 48,22" fill="none" stroke="#a5b4fc" strokeWidth="2"/>
            <polyline points="12,35 24,30 36,35 48,32" fill="none" stroke="#c4b5fd" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "hive-plot":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-yellow-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <line x1="32" y1="24" x2="32" y2="8" stroke="#eab308" strokeWidth="2"/>
            <line x1="32" y1="24" x2="48" y2="36" stroke="#eab308" strokeWidth="2"/>
            <line x1="32" y1="24" x2="16" y2="36" stroke="#eab308" strokeWidth="2"/>
            <circle cx="32" cy="12" r="2" fill="#ca8a04"/>
            <circle cx="32" cy="16" r="2" fill="#d97706"/>
            <circle cx="44" cy="32" r="2" fill="#ca8a04"/>
            <circle cx="40" cy="36" r="2" fill="#d97706"/>
            <circle cx="20" cy="32" r="2" fill="#ca8a04"/>
            <circle cx="24" cy="36" r="2" fill="#d97706"/>
            <path d="M32,12 Q38,20 44,32" fill="none" stroke="#facc15" strokeWidth="1"/>
            <path d="M32,16 Q26,26 20,32" fill="none" stroke="#fde047" strokeWidth="1"/>
          </svg>
        </div>
      );

    // Simple layouts & docs
    case "cards-panels":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-slate-100 to-slate-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="20" height="16" rx="2" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1"/>
            <rect x="36" y="8" width="20" height="16" rx="2" fill="#f1f5f9" stroke="#94a3b8" strokeWidth="1"/>
            <rect x="8" y="28" width="20" height="16" rx="2" fill="#e2e8f0" stroke="#64748b" strokeWidth="1"/>
            <rect x="36" y="28" width="20" height="16" rx="2" fill="#f8fafc" stroke="#cbd5e1" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "iconic-infographic":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-orange-100 to-orange-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="16" cy="16" r="6" fill="#f97316"/>
            <rect x="32" y="10" width="12" height="12" fill="#fb923c"/>
            <polygon points="52,10 58,22 46,22" fill="#fed7aa"/>
            <rect x="8" y="28" width="48" height="3" fill="#ea580c"/>
            <rect x="12" y="34" width="8" height="8" fill="#fdba74"/>
            <circle cx="32" cy="38" r="4" fill="#f97316"/>
            <polygon points="48,34 54,42 42,42" fill="#fb923c"/>
          </svg>
        </div>
      );
    case "checklist":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-green-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="12" width="4" height="4" fill="none" stroke="#16a34a" strokeWidth="1"/>
            <rect x="8" y="20" width="4" height="4" fill="none" stroke="#16a34a" strokeWidth="1"/>
            <rect x="8" y="28" width="4" height="4" fill="none" stroke="#16a34a" strokeWidth="1"/>
            <rect x="8" y="36" width="4" height="4" fill="none" stroke="#16a34a" strokeWidth="1"/>
            <path d="M9,14 L10.5,15.5 L13,13" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <path d="M9,22 L10.5,23.5 L13,21" stroke="#22c55e" strokeWidth="2" fill="none"/>
            <line x1="16" y1="14" x2="50" y2="14" stroke="#4ade80" strokeWidth="2"/>
            <line x1="16" y1="22" x2="50" y2="22" stroke="#4ade80" strokeWidth="2"/>
            <line x1="16" y1="30" x2="50" y2="30" stroke="#86efac" strokeWidth="1"/>
            <line x1="16" y1="38" x2="50" y2="38" stroke="#86efac" strokeWidth="1"/>
          </svg>
        </div>
      );
    case "numbered-list":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-blue-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <circle cx="12" cy="14" r="3" fill="#3b82f6"/>
            <circle cx="12" cy="22" r="3" fill="#60a5fa"/>
            <circle cx="12" cy="30" r="3" fill="#93c5fd"/>
            <circle cx="12" cy="38" r="3" fill="#dbeafe"/>
            <text x="12" y="17" fontSize="6" fill="white" textAnchor="middle">1</text>
            <text x="12" y="25" fontSize="6" fill="white" textAnchor="middle">2</text>
            <text x="12" y="33" fontSize="6" fill="white" textAnchor="middle">3</text>
            <text x="12" y="41" fontSize="6" fill="white" textAnchor="middle">4</text>
            <line x1="18" y1="14" x2="50" y2="14" stroke="#3b82f6" strokeWidth="2"/>
            <line x1="18" y1="22" x2="50" y2="22" stroke="#60a5fa" strokeWidth="2"/>
            <line x1="18" y1="30" x2="50" y2="30" stroke="#93c5fd" strokeWidth="2"/>
            <line x1="18" y1="38" x2="50" y2="38" stroke="#dbeafe" strokeWidth="2"/>
          </svg>
        </div>
      );
    case "scorecard":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" rx="4" fill="#f0fdf4" stroke="#16a34a" strokeWidth="2"/>
            <rect x="12" y="12" width="20" height="8" fill="#dcfce7"/>
            <rect x="36" y="12" width="16" height="8" fill="#bbf7d0"/>
            <rect x="12" y="24" width="20" height="8" fill="#86efac"/>
            <rect x="36" y="24" width="16" height="8" fill="#4ade80"/>
            <rect x="12" y="32" width="40" height="4" fill="#22c55e"/>
            <text x="22" y="17" fontSize="6" fill="#15803d">KPI</text>
            <text x="44" y="17" fontSize="6" fill="#15803d">95%</text>
          </svg>
        </div>
      );
    case "risk-matrix":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-red-100 to-yellow-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" fill="none" stroke="#6b7280" strokeWidth="2"/>
            <line x1="24" y1="8" x2="24" y2="40" stroke="#6b7280" strokeWidth="1"/>
            <line x1="40" y1="8" x2="40" y2="40" stroke="#6b7280" strokeWidth="1"/>
            <line x1="8" y1="18.7" x2="56" y2="18.7" stroke="#6b7280" strokeWidth="1"/>
            <line x1="8" y1="29.3" x2="56" y2="29.3" stroke="#6b7280" strokeWidth="1"/>
            <rect x="8" y="8" width="16" height="10.7" fill="#fef3c7"/>
            <rect x="24" y="8" width="16" height="10.7" fill="#fed7aa"/>
            <rect x="40" y="8" width="16" height="10.7" fill="#fecaca"/>
            <rect x="8" y="18.7" width="16" height="10.6" fill="#fed7aa"/>
            <rect x="24" y="18.7" width="16" height="10.6" fill="#fecaca"/>
            <rect x="40" y="18.7" width="16" height="10.6" fill="#dc2626"/>
            <rect x="8" y="29.3" width="16" height="10.7" fill="#fecaca"/>
            <rect x="24" y="29.3" width="16" height="10.7" fill="#dc2626"/>
            <rect x="40" y="29.3" width="16" height="10.7" fill="#991b1b"/>
          </svg>
        </div>
      );
    case "priority-matrix":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" fill="none" stroke="#6b7280" strokeWidth="2"/>
            <line x1="32" y1="8" x2="32" y2="40" stroke="#6b7280" strokeWidth="2"/>
            <line x1="8" y1="24" x2="56" y2="24" stroke="#6b7280" strokeWidth="2"/>
            <rect x="32" y="8" width="24" height="16" fill="#dcfce7"/>
            <rect x="8" y="8" width="24" height="16" fill="#dbeafe"/>
            <rect x="32" y="24" width="24" height="16" fill="#fef3c7"/>
            <rect x="8" y="24" width="24" height="16" fill="#fecaca"/>
            <circle cx="44" cy="16" r="2" fill="#16a34a"/>
            <circle cx="20" cy="16" r="2" fill="#3b82f6"/>
            <circle cx="44" cy="32" r="2" fill="#eab308"/>
            <circle cx="20" cy="32" r="2" fill="#dc2626"/>
          </svg>
        </div>
      );
    case "moscow-matrix":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-red-100 to-green-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" fill="none" stroke="#6b7280" strokeWidth="2"/>
            <line x1="32" y1="8" x2="32" y2="40" stroke="#6b7280" strokeWidth="1"/>
            <line x1="8" y1="24" x2="56" y2="24" stroke="#6b7280" strokeWidth="1"/>
            <rect x="32" y="8" width="24" height="16" fill="#fecaca"/>
            <rect x="8" y="8" width="24" height="16" fill="#fed7aa"/>
            <rect x="32" y="24" width="24" height="16" fill="#fef3c7"/>
            <rect x="8" y="24" width="24" height="16" fill="#dcfce7"/>
            <text x="44" y="17" fontSize="8" fill="#dc2626" textAnchor="middle">M</text>
            <text x="20" y="17" fontSize="8" fill="#ea580c" textAnchor="middle">S</text>
            <text x="44" y="33" fontSize="8" fill="#eab308" textAnchor="middle">C</text>
            <text x="20" y="33" fontSize="8" fill="#16a34a" textAnchor="middle">W</text>
          </svg>
        </div>
      );
    case "raci-matrix":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-purple-100 to-purple-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="48" height="32" fill="none" stroke="#8b5cf6" strokeWidth="2"/>
            <rect x="8" y="8" width="48" height="8" fill="#ddd6fe"/>
            <line x1="20" y1="8" x2="20" y2="40" stroke="#8b5cf6" strokeWidth="1"/>
            <line x1="32" y1="8" x2="32" y2="40" stroke="#8b5cf6" strokeWidth="1"/>
            <line x1="44" y1="8" x2="44" y2="40" stroke="#8b5cf6" strokeWidth="1"/>
            <line x1="8" y1="20" x2="56" y2="20" stroke="#8b5cf6" strokeWidth="1"/>
            <line x1="8" y1="26" x2="56" y2="26" stroke="#8b5cf6" strokeWidth="1"/>
            <line x1="8" y1="32" x2="56" y2="32" stroke="#8b5cf6" strokeWidth="1"/>
            <text x="14" y="24" fontSize="6" fill="#7c3aed" textAnchor="middle">R</text>
            <text x="26" y="24" fontSize="6" fill="#8b5cf6" textAnchor="middle">A</text>
            <text x="38" y="30" fontSize="6" fill="#a78bfa" textAnchor="middle">C</text>
            <text x="50" y="36" fontSize="6" fill="#c4b5fd" textAnchor="middle">I</text>
          </svg>
        </div>
      );
    case "storyboard":
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-amber-100 to-amber-50 rounded mb-2 flex items-center justify-center">
          <svg viewBox="0 0 64 48" className="w-full h-full">
            <rect x="8" y="8" width="14" height="10" rx="1" fill="#fef3c7" stroke="#f59e0b" strokeWidth="1"/>
            <rect x="25" y="8" width="14" height="10" rx="1" fill="#fde68a" stroke="#f59e0b" strokeWidth="1"/>
            <rect x="42" y="8" width="14" height="10" rx="1" fill="#fcd34d" stroke="#f59e0b" strokeWidth="1"/>
            <rect x="8" y="25" width="14" height="10" rx="1" fill="#fbbf24" stroke="#f59e0b" strokeWidth="1"/>
            <rect x="25" y="25" width="14" height="10" rx="1" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1"/>
            <rect x="42" y="25" width="14" height="10" rx="1" fill="#d97706" stroke="#f59e0b" strokeWidth="1"/>
            <text x="15" y="21" fontSize="4" fill="#92400e" textAnchor="middle">1</text>
            <text x="32" y="21" fontSize="4" fill="#92400e" textAnchor="middle">2</text>
            <text x="49" y="21" fontSize="4" fill="#92400e" textAnchor="middle">3</text>
            <text x="15" y="38" fontSize="4" fill="#92400e" textAnchor="middle">4</text>
            <text x="32" y="38" fontSize="4" fill="#92400e" textAnchor="middle">5</text>
            <text x="49" y="38" fontSize="4" fill="#92400e" textAnchor="middle">6</text>
          </svg>
        </div>
      );
    default:
      return (
        <div className="w-16 h-12 bg-gradient-to-br from-primary/20 to-secondary/20 rounded mb-2 flex items-center justify-center">
          <Target className="w-6 h-6 text-primary" />
        </div>
      );
  }
};

interface AdditionalVisualDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onGenerate: (prompt: string, visualType: string) => void;
  isGenerating: boolean;
  humanizedText: string;
}

export const AdditionalVisualDialog = ({
  open,
  onOpenChange,
  onGenerate,
  isGenerating,
  humanizedText
}: AdditionalVisualDialogProps) => {
  const [prompt, setPrompt] = useState("");
  const [selectedVisualType, setSelectedVisualType] = useState("auto");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const handleGenerate = () => {
    const finalPrompt = prompt.trim() || humanizedText;
    onGenerate(finalPrompt, selectedVisualType);
  };

  const filteredVisualTypes = selectedCategory 
    ? visualTypes.filter(v => v.category === selectedCategory)
    : visualTypes;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Generate Additional Visual
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {/* Custom Prompt */}
          <div className="space-y-2">
            <Label htmlFor="prompt">Custom Prompt (optional)</Label>
            <Textarea
              id="prompt"
              placeholder="Describe what you want to visualize, or leave empty to use the humanized text..."
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              className="min-h-[100px]"
            />
            <p className="text-sm text-muted-foreground">
              Leave empty to generate a visual based on your humanized text
            </p>
          </div>

          {/* Visual Type Selection */}
          <div className="space-y-4">
            <Label>Visual Type</Label>
            
            {/* Auto Option */}
            <Card 
              className={`cursor-pointer transition-all ${selectedVisualType === 'auto' ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
              onClick={() => setSelectedVisualType('auto')}
            >
              <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-primary" />
                    <div>
                      <h3 className="font-medium">Auto Select</h3>
                      <p className="text-sm text-muted-foreground">AI will choose the best visual type for your content</p>
                    </div>
                    {selectedVisualType === 'auto' && (
                      <Badge variant="secondary" className="ml-auto">Selected</Badge>
                    )}
                  </div>
              </CardContent>
            </Card>

            {/* Category Filter */}
            <div className="space-y-2">
              <Label>Filter by Category</Label>
              <Select value={selectedCategory || "all"} onValueChange={(value) => setSelectedCategory(value === "all" ? null : value)}>
                <SelectTrigger>
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {categories.map(category => (
                    <SelectItem key={category} value={category}>{category}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Visual Type Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-[300px] overflow-y-auto">
              {filteredVisualTypes.map((visualType) => (
                <Card
                  key={visualType.id}
                  className={`cursor-pointer transition-all ${selectedVisualType === visualType.id ? 'ring-2 ring-primary bg-primary/5' : 'hover:bg-muted/50'}`}
                  onClick={() => setSelectedVisualType(visualType.id)}
                >
                  <CardContent className="p-3 text-center">
                    {getPreviewSvg(visualType.id)}
                    <div className="text-sm font-medium">{visualType.name}</div>
                    <div className="text-xs text-muted-foreground mt-1">{visualType.category}</div>
                    {selectedVisualType === visualType.id && (
                      <Badge variant="secondary" className="mt-2">Selected</Badge>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {/* Generate Button */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isGenerating}
            >
              Cancel
            </Button>
            <Button
              onClick={handleGenerate}
              disabled={isGenerating}
              className="min-w-[120px]"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Generate Visual
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};